import { useLocalStorage } from "hooks/useLocalStorage"
import { type SetStateAction, type Dispatch, useCallback, useMemo } from "react"

type BaseRecentSearchCommand = {
  count: number
  lastUsed: number
  id: string
}

type AskRecentSearchCommand = BaseRecentSearchCommand & {
  type: "ask"
  query: string
}

export type RecentSearchCommand =
  | BaseRecentSearchCommand
  | AskRecentSearchCommand

export const isAskCommand = (
  command: RecentSearchCommand
): command is AskRecentSearchCommand => {
  return "type" in command && command.type === "ask"
}

// TODO: rip this out. A temporary function to filter out old commands from the old recent command shape.
const filterOutOldCommands = (
  commands: Record<string, RecentSearchCommand>
) => {
  return Object.fromEntries(
    Object.entries(commands).filter(([_, command]) => command.id)
  )
}

export const useRecentSearchCommands = () => {
  const [recentSearchCommands, setRecentSearchCommands] = useLocalStorage<
    Record<string, RecentSearchCommand>
  >("recentSearchCommands", {}) as [
    Record<string, RecentSearchCommand>,
    Dispatch<SetStateAction<Record<string, RecentSearchCommand>>>,
  ]

  const addRecentSearchCommand = useCallback(
    (commandId: string, type?: "ask", query?: string) => {
      setRecentSearchCommands((prev) => {
        let searchCommand: RecentSearchCommand = {
          count: (prev[commandId]?.count || 0) + 1,
          lastUsed: Date.now(),
          id: commandId,
        }

        // For dynamic commands, we can optionall store some extra payload to help us render it again
        if (type === "ask") {
          searchCommand = {
            ...searchCommand,
            type: "ask",
            query,
          }
        }

        // If we have more than 5 items, remove the oldest ones
        const updatedCommands = {
          ...filterOutOldCommands(prev),
          [commandId]: searchCommand,
        }
        const entries = Object.entries(updatedCommands)
        if (entries.length > 5) {
          entries.sort((a, b) => a[1].lastUsed - b[1].lastUsed)
          return Object.fromEntries(entries.slice(entries.length - 5))
        }

        return updatedCommands
      })
    },
    [setRecentSearchCommands]
  )

  const removeRecentSearchCommand = useCallback(
    (commandId: string) => {
      setRecentSearchCommands((prev) => {
        const { [commandId]: _, ...rest } = prev
        return rest
      })
    },
    [setRecentSearchCommands]
  )

  return useMemo(
    () => ({
      recentSearchCommands: filterOutOldCommands(recentSearchCommands),
      addRecentSearchCommand,
      removeRecentSearchCommand,
    }),
    [recentSearchCommands, addRecentSearchCommand, removeRecentSearchCommand]
  )
}
